import {
  AppVersion,
  Colors,
  getMediaDevices,
  PopupButtonType,
  PopupIconType,
  PopupOnCloseData,
  showPopup
} from "@kalyzee/kast-web-sdk/web-components";
import {
  SettingsForm,
  SettingsFormRef,
  SettingsFormChangeData,
} from "@kalyzee/kast-app-web-components";
import React, { ForwardedRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Settings } from "../../helpers/settings";
import { useSettings } from "../../hooks/settings";
import { useRequestAdminPin } from "../../hooks/utils";
import {
  settingsSchema,
  SETTINGS_SCHEMA_VERSION,
  exportSettings,
  importSettings,
  prepareSettingsForForm,
  restoreSettingsFromForm,
} from "../../helpers/settingsSchema";
import styles from "./SettingsPopup.module.css";
import workBoxStyles from "../workBoxes/WorkBox.module.css";
import Versions from "./Versions";
import { BoxOverlay, BoxContainer, OverlayContainer, Button, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { addPopup, removePopup } from "../../helpers/popup";
import { syntaxHighlightJson } from "../../helpers/json";

export interface SettingsPopupProps {}

export interface SettingsPopupRef {
  openPopup: () => void;
}

const SettingsPopup = React.forwardRef(({}: SettingsPopupProps, forwardRef: ForwardedRef<SettingsPopupRef | undefined>) => {
  const [settings, setSettings] = useSettings();
  const requestAdminPin = useRequestAdminPin();
  const [isOpen, setIsOpen] = useState(false);

  const settingsFormRef = useRef<SettingsFormRef | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formValidRef = useRef(true);
  const formErrorsRef = useRef<{ path: string; message: string }[]>([]);

  const tryOpenPopup = () => {
    requestAdminPin((success) => {
      if (success) setIsOpen(true);
    });
  };

  const showErrorPopup = (title: string, content: React.ReactNode) => {
    showPopup({
      title,
      iconTitle: PopupIconType.ERROR,
      content,
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const showWarningsPopup = (title: string, warnings: string[], onContinue: () => void) => {
    showPopup({
      title,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div className={styles.warningsContent}>
          <div className={styles.warningsList}>
            {warnings.map((w, i) => (
              <div key={i} className={styles.warningItem}>• {w}</div>
            ))}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Annuler" },
        {
          type: PopupButtonType.VALIDATE,
          element: <div style={{ color: Colors.getMainGreen() }}>Continuer quand même</div>,
        },
      ],
      onClose: (data: PopupOnCloseData) => {
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          onContinue();
        }
      },
    });
  };

  const doSave = useCallback(() => {
    let newSettings: Settings | undefined;
    let error: string | undefined;
    try {
      const jsonStr = settingsFormRef.current?.getJsonString() ?? '{}';
      const parsed = JSON.parse(jsonStr);
      const restored = restoreSettingsFromForm(parsed, settingsSchema);
      newSettings = setSettings(restored, { save: true });
    } catch (err) {
      error = (err as any)?.message;
    }

    if (newSettings) {
      let popup: React.ReactNode;
      popup = (
        <OverlayContainer
          visible
          style={{ width: "100vw", height: "100vh" }}
          overlay={
            <div onClick={(e) => e.stopPropagation()}>
              <BoxOverlay
                title="Voulez-vous rafraîchir la page pour appliquer vos modifications ?"
                level="success"
                buttons={[
                  { label: "Non", onClick: () => removePopup(popup) },
                  { label: "Oui, rafraîchir", style: { backgroundColor: "var(--color-green-1)" }, onClick: () => { removePopup(popup); window.location.reload(); } },
                ]}
              />
            </div>
          }
        />
      );
      addPopup(popup, "settings_apply_changes", "settings");
    } else {
      showErrorPopup(
        "Impossible de sauvegarder les paramètres",
        "Une erreur est apparue durant la sauvegarde des paramètres : " + error,
      );
    }
  }, [setSettings]);

  const trySave = useCallback((): boolean => {
    if (!formValidRef.current) {
      showErrorPopup(
        "Impossible de sauvegarder",
        <div className={styles.warningsContent}>
          <div style={{ marginBottom: 8 }}>Corrigez les erreurs suivantes :</div>
          <div className={styles.warningsList}>
            {formErrorsRef.current.map((e, i) => (
              <div key={i} className={styles.warningItem}>• <strong>{e.path}</strong>: {e.message}</div>
            ))}
          </div>
        </div>,
      );
      return false;
    }
    return true;
  }, []);

  const handleFormChange = useCallback((data: SettingsFormChangeData) => {
    formValidRef.current = data.valid;
    formErrorsRef.current = data.errors;
  }, []);

  const handleExport = useCallback(() => {
    try {
      const currentValue = settingsFormRef.current?.getValue() ?? {};
      exportSettings(restoreSettingsFromForm(currentValue, settingsSchema));
    } catch (err) {
      showErrorPopup("Erreur d'export", "Impossible d'exporter les paramètres : " + (err as any)?.message);
    }
  }, []);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const result = await importSettings(file);
      const allWarnings: string[] = [];
      if (result.versionWarning) allWarnings.push(result.versionWarning);
      allWarnings.push(...result.warnings);

      const applyImport = () => {
        settingsFormRef.current?.setValue(JSON.stringify(result.settings));
      };

      if (allWarnings.length > 0) {
        showWarningsPopup("Avertissements à l'import", allWarnings, applyImport);
      } else {
        applyImport();
      }
    } catch (err) {
      showErrorPopup("Erreur d'import", (err as Error).message);
    }
  }, []);

  const handleClose = useCallback(() => {
    let popup: React.ReactNode;
    const dismiss = () => { removePopup(popup); setIsOpen(false); };
    const closePopupOnly = () => { removePopup(popup); };
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100vw", height: "100vh" }}
        onClose={closePopupOnly}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Voulez-vous sauvegarder avant de fermer ?"
              level="question"
              buttons={[
                {
                  label: "Non, fermer",
                  onClick: dismiss,
                },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => {
                    removePopup(popup);
                    if (trySave()) {
                      setIsOpen(false);
                      doSave();
                    }
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_save_confirm", "settings");
  }, [trySave, doSave]);

  const handleSave = useCallback(() => {
    if (trySave()) {
      setIsOpen(false);
      doSave();
    }
  }, [trySave, doSave]);

  useImperativeHandle(forwardRef, () => ({
    openPopup: tryOpenPopup,
  }));

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          Paramètres
          <span className={styles.headerVersion}>v{SETTINGS_SCHEMA_VERSION}</span>
        </div>
        <div className={styles.headerActions}>
          <Button.Simple
            label="Media Devices"
            className={styles.actionBtn}
            onClick={async () => {
              const devices = await getMediaDevices("both", "input");
              let popup: React.ReactNode;
              popup = (
                <OverlayContainer
                  visible
                  style={{ width: "100vw", height: "100vh" }}
                  onClose={() => removePopup(popup)}
                  overlay={
                    <div onClick={(e) => e.stopPropagation()} className={styles.devicesWorkBoxWrapper}>
                      <BoxContainer
                        topBarProps={{
                          left: [],
                          center: [<span key="title" className={workBoxStyles.label}>Media Devices</span>],
                          right: [
                            <PrimaryButton key="close" onClick={() => removePopup(popup)}>
                              <ReactIcons.Close style={{ height: "0.8em" }} />
                            </PrimaryButton>,
                          ],
                        }}
                      >
                        <div className={styles.devicesWorkBoxContent}>
                          <pre className={styles.devicesWorkBoxPre} dangerouslySetInnerHTML={{ __html: syntaxHighlightJson(devices) }} />
                        </div>
                        <div className={styles.devicesWorkBoxFooter}>
                          <Button.Simple label="Fermer" onClick={() => { removePopup(popup); }} />
                        </div>
                      </BoxContainer>
                    </div>
                  }
                />
              );
              addPopup(popup, "settings_media_devices", "settings");
            }}
          />
          <Button.Simple label="Exporter JSON" className={styles.actionBtn} onClick={handleExport} />
          <Button.Simple label="Importer JSON" className={styles.actionBtn} onClick={handleImportClick} />
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleFileSelected as any}
          />
        </div>
      </div>

      {/* Body — scrollable form */}
      <div className={styles.body}>
        <div className={styles.settingsFormContainer}>
          <SettingsForm
            ref={settingsFormRef}
            schema={settingsSchema}
            defaultValue={JSON.stringify(prepareSettingsForForm(settings, settingsSchema))}
            onChange={handleFormChange}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerVersions}>
          <Versions />
          <AppVersion />
        </div>
        <div className={styles.footerButtons}>
          <Button.Simple label="Fermer" className={styles.actionBtn} onClick={handleClose} />
          <Button.Simple label="Sauvegarder" className={styles.actionBtn} onClick={handleSave} />
        </div>
      </div>
    </div>
  );
});

export default SettingsPopup;
